import { useEffect, useRef } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useForm, Form } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import { PlusIcon } from 'Asset/icon';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonLink from 'UiKit/button/buttonLink';
import ButtonSolid from 'UiKit/button/buttonSolid';
import PreviewImage from 'UiKit/preview-image';
import { TextBold, TextSemiBold } from 'UiKit/typography';
import { Condition, Iteration } from 'Component/others';
import { getMenuPermissions } from 'Helper/getMenuPermissions';

import usePatchTalkingPoint from 'Domain/brief-live/usePatchTalkingPoint';
import usePostTalkingPoint from 'Domain/brief-live/usePostTalkingPoint';
import useGetTalkingPointDetail from 'Domain/brief-live/useGetTalkingPointDetail';
import useGetBrand from 'Domain/brand/useGetBrand';

import { useTalkingPointUpsertState } from 'View/brief-live/state/talkingPointState';
import StatusBadge from 'View/brief-live/component/status-badge';
import TitleBriefCard from 'View/brief-live/component/talking-point-upsert/title-brief-card';
import ProductHighlightCard from 'View/brief-live/component/talking-point-upsert/product-highlight-card';
import BriefSectionCard from 'View/brief-live/component/talking-point-upsert/brief-section-card';
import BriefConfigCard from 'View/brief-live/component/talking-point-upsert/brief-config-card';
import populateTalkingPointFormFields from 'View/brief-live/helper/populateTalkingPointFormFields';
import checkFormUpsertValidation from 'View/brief-live/helper/checkFormUpsertValidation';
import applyTalkingPointFormCache from 'View/brief-live/helper/applyTalkingPointFormCache';
import SelectProductModal from 'View/brief-live/component/select-product-modal';

import { getPublishStatus, getTalkingPointStatuses } from 'View/brief-live/const/statusConfig';
import { DEFAULT_FORM_VALUES } from './const';

import styles from './styles.module.css';

const TalkingPointFormUpsert = () => {
  const { t } = useTranslation('desktop-brief-live');

  // Form
  const { control, formState, register, watch, handleSubmit, clearErrors, setValue, trigger, getValues } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });
  const { errors, isSubmitting, isDirty } = formState;
  const formField = watch();

  const navigate = useNavigate();
  const { id } = useParams();

  const isEditForm = !isEmpty(id);
  const isCreateForm = isEmpty(id);

  // Caching
  const { saveCache, clearCache, clearCacheForId, getCache } = applyTalkingPointFormCache(id);
  const prevIdRef = useRef(id);

  const accessPermission = getMenuPermissions('others', 'briefs');
  const { state, updateState, filterState, updateFilterState } = useTalkingPointUpsertState();
  const { selectedImagePreview, isOpenImagePreview, isOpenModalSelectProduct } = state;

  const { data: optionsBrand } = useGetBrand();
  const { data: selectedTalkingPoint, isLoading: isLoadingTalkingPointDetail } = useGetTalkingPointDetail(id);
  const { mutate: postTalkingPoint, isPending: isPostingTalkingPoint } = usePostTalkingPoint();
  const { mutate: patchTalkingPoint, isPending: isPatchingTalkingPoint } = usePatchTalkingPoint();

  const formTitle = t(isEditForm ? 'formTalkingPoint.edit' : 'formTalkingPoint.create');
  const talkingPointStatus = selectedTalkingPoint?.rawData?.status;
  const talkingPointPublishStatus = selectedTalkingPoint?.rawData?.isPublished;

  const { isValidSubmit, isValidDraft } = checkFormUpsertValidation({ formField, errorFields: errors });

  const isDraftDisabled = isSubmitting || isPatchingTalkingPoint || !isValidDraft;
  const isSaveDisabled = isSubmitting || isPostingTalkingPoint || !isValidSubmit || !isDirty;

  const handleCancel = () => {
    clearCache();
    navigate('/brief-live');
  };

  const handleClosePreview = () => {
    const previousModal = state.imagePreviewPreviousModal;
    updateState({
      isOpenImagePreview: false,
      selectedImagePreview: [],
      imagePreviewPreviousModal: null,
      ...(previousModal ? { [previousModal]: true } : {}),
    });
  };

  const onChangeInlineField = (event) => {
    clearErrors(event?.target?.name);
    setValue(event?.target?.name, event?.target?.value, { shouldDirty: true });
  };

  const onSubmit = () => {
    clearCache();
    return isCreateForm ? postTalkingPoint({ formData: formField, isPublished: true }) : patchTalkingPoint({ formData: formField, id, isPublished: true });
  };

  const onSubmitDraft = () => {
    clearCache();
    return isCreateForm ? postTalkingPoint({ formData: formField, isPublished: false }) : patchTalkingPoint({ formData: formField, id, isPublished: false });
  };

  // Product highlight handlers
  const handleSelectProductConfirm = (items) => {
    if (!items?.length) return;
    const mapped = items.map((p) => ({ id: p.id, label: p.productName, value: p.skuCode, image: p.image }));
    setValue('productHighlight', [...(formField?.productHighlight || []), ...mapped], { shouldDirty: true });
  };
  const handleCreateManualProduct = (product) => {
    setValue('productHighlight', [...(formField?.productHighlight || []), product], { shouldDirty: true });
  };
  const handleDeleteProduct = (product) => {
    setValue(
      'productHighlight',
      (formField?.productHighlight || []).filter((p) => p.value !== product.value),
      { shouldDirty: true },
    );
  };
  const handleEditProduct = (product, updates) => {
    setValue(
      'productHighlight',
      (formField?.productHighlight || []).map((p) => (p.value === product.value ? { ...p, ...updates } : p)),
      { shouldDirty: true },
    );
  };

  // Brief section handlers
  const handleAddBriefSection = () => {
    setValue('sections', [...(formField?.sections || []), { title: '', description: '', orderIndex: (formField?.sections?.length || 0) + 1 }], {
      shouldDirty: true,
    });
  };

  const handleRemoveSection = (index) => {
    setValue(
      'sections',
      (formField?.sections || []).filter((_, i) => i !== index),
      { shouldDirty: true },
    );
  };

  const handleChangeDescription = (index, value) => {
    setValue(`sections.${index}.description`, value, { shouldDirty: true });
  };

  useEffect(() => {
    if (isEmpty(accessPermission)) navigate('/forbidden');
  }, [accessPermission]); // eslint-disable-line

  useEffect(() => {
    if (!isCreateForm) return;
    const cached = getCache();
    if (!cached) return;
    Object.keys(cached).forEach((key) => setValue(key, cached[key], { shouldDirty: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isCreateForm && selectedTalkingPoint) {
      populateTalkingPointFormFields({ setValue, selectedTalkingPoint, optionsBrand });
      const cached = getCache();
      if (cached) {
        Object.keys(cached).forEach((key) => setValue(key, cached[key], { shouldDirty: true }));
      }
    }
  }, [isCreateForm, selectedTalkingPoint, optionsBrand, setValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prevId = prevIdRef.current;
    if (prevId !== id) {
      clearCacheForId(prevId);
      prevIdRef.current = id;
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box className={styles['form-upsert__container']}>
      <Box className={styles['form-upsert__header']}>
        <Flex gap={'12px'}>
          <TextBold className={styles['form-upsert__title']}>{formTitle}</TextBold>
          <Condition statement={isEditForm}>
            <StatusBadge {...(getPublishStatus({ isPublished: talkingPointPublishStatus, t }) || {})} />
            <StatusBadge {...(getTalkingPointStatuses({ status: talkingPointStatus, t }) || {})} />
          </Condition>
        </Flex>

        <Box className={styles['form-upsert__header-action']}>
          <ButtonOutline className={styles['form-upsert__button--cancel']} onClick={handleCancel} type="button" disabled={isSubmitting}>
            <TextSemiBold>{t('formTalkingPoint.buttonCancel')}</TextSemiBold>
          </ButtonOutline>

          <ButtonOutline
            className={styles['form-upsert__button--save-draft']}
            type="button"
            isLoading={isSubmitting || isPatchingTalkingPoint}
            onClick={onSubmitDraft}
            disabled={isDraftDisabled}
          >
            <TextSemiBold>{t('formTalkingPoint.buttonSaveDraft')}</TextSemiBold>
          </ButtonOutline>

          <ButtonSolid
            className={styles['form-upsert__button--save']}
            type="button"
            isLoading={isSubmitting || isPostingTalkingPoint}
            onClick={onSubmit}
            disabled={isSaveDisabled}
          >
            <TextSemiBold className={styles['form-upsert__button-save-text']}>{t('formTalkingPoint.buttonSave')}</TextSemiBold>
          </ButtonSolid>
        </Box>
      </Box>

      <Box className={styles['form-upsert__body']}>
        <Form control={control} className={styles['form-upsert__section--container']} onSubmit={handleSubmit(onSubmit)} onBlur={() => saveCache(getValues())}>
          <Box className={styles['form-upsert__section--left']}>
            <BriefConfigCard
              control={control}
              errors={errors}
              formField={formField}
              optionsBrand={optionsBrand}
              isLoadingTalkingPointDetail={isLoadingTalkingPointDetail}
              setValue={setValue}
              t={t}
            />
          </Box>

          <Box className={styles['form-upsert__section--right']}>
            <TitleBriefCard
              value={formField?.title}
              error={errors?.title}
              isFetching={isLoadingTalkingPointDetail}
              t={t}
              register={{
                ...register('title'),
                onChange: (event) => {
                  event.target.value = event.target.value.slice(0, 225);
                  onChangeInlineField(event);
                },
              }}
            />

            <ProductHighlightCard
              onChange={handleCreateManualProduct}
              brand={formField?.brand}
              products={formField?.productHighlight}
              onDelete={handleDeleteProduct}
              onEdit={handleEditProduct}
              t={t}
            />

            <Iteration
              items={formField?.sections}
              render={(section, index) => (
                <BriefSectionCard
                  section={section}
                  error={errors?.sections?.[index]}
                  isFetching={isLoadingTalkingPointDetail}
                  register={{
                    ...register(`sections.${index}.title`),
                    onChange: (event) => {
                      event.target.value = event.target.value.slice(0, 225);
                      onChangeInlineField(event);
                      trigger(`sections.${index}.title`);
                    },
                  }}
                  onChangeDescription={(value) => handleChangeDescription(index, value)}
                  sectionsLength={formField?.sections?.length}
                  onRemove={() => handleRemoveSection(index)}
                  t={t}
                />
              )}
            />

            <ButtonLink onClick={handleAddBriefSection}>
              <PlusIcon /> {t('formTalkingPoint.addSection')}
            </ButtonLink>
          </Box>
        </Form>
      </Box>

      <PreviewImage images={selectedImagePreview} isOpen={isOpenImagePreview} onClose={handleClosePreview} initialIndex={0} />
      <SelectProductModal
        isOpen={isOpenModalSelectProduct}
        updateState={updateState}
        filterState={filterState}
        updateFilterState={updateFilterState}
        onConfirm={handleSelectProductConfirm}
        existingProducts={formField?.productHighlight}
        t={t}
      />
    </Box>
  );
};

export default TalkingPointFormUpsert;
